import { createSvgEl } from '@learnforge/demo-render';
import type { NumberSet } from '../sets';

// ── Layout constants ─────────────────────────────────────────────────────────

/** Viewbox dimensions (set by the parent SVG if one is created here). */
const W = 400;
const H = 300;

/**
 * The 5 standard number sets in containment order (innermost → outermost).
 * ℕ ⊂ ℤ ⊂ ℚ ⊂ ℝ ⊂ ℂ
 */
const SETS: { id: NumberSet; glyph: string }[] = [
  { id: 'N', glyph: 'ℕ' },
  { id: 'Z', glyph: 'ℤ' },
  { id: 'Q', glyph: 'ℚ' },
  { id: 'R', glyph: 'ℝ' },
  { id: 'C', glyph: 'ℂ' },
];

/**
 * Returns the set S itself and all sets that CONTAIN S in the chain ℕ⊂ℤ⊂ℚ⊂ℝ⊂ℂ.
 * e.g. supersets('R') → ['R', 'C']
 *      supersets('N') → ['N', 'Z', 'Q', 'R', 'C']
 */
function supersetsOf(s: NumberSet): NumberSet[] {
  const idx = SETS.findIndex((e) => e.id === s);
  if (idx < 0) return [];
  return SETS.slice(idx).map((e) => e.id);
}

// ── Public API ───────────────────────────────────────────────────────────────

export interface NumberNestingOptions {
  highlight?: NumberSet | null;
}

export interface NumberNestingHandle {
  group: SVGGElement;
  update(opts: Partial<NumberNestingOptions>): void;
  destroy(): void;
}

/**
 * Renders five nested rounded-rect regions for ℕ⊂ℤ⊂ℚ⊂ℝ⊂ℂ as an SVG group.
 *
 * - Each set gets a `<g data-set="N|Z|Q|R|C">` with a rounded rect + glyph label.
 * - `highlight = S` adds `is-highlight` to S and all sets that contain S.
 * - No inline colours; all styling via CSS classes.
 */
export function createNumberNesting(
  parent: SVGSVGElement | SVGGElement,
  opts: NumberNestingOptions,
): NumberNestingHandle {
  let current: Required<NumberNestingOptions> = { highlight: opts.highlight ?? null };

  const group = createSvgEl('g', { class: 'nn-nesting' });
  parent.appendChild(group);

  // Compute per-set rect geometry: evenly nested, outermost (ℂ) largest.
  // We lay out 5 ellipses/rects concentrically. Each level adds a margin.
  const PAD = 32;
  const cx = W / 2;
  const cy = H / 2;
  // ℂ is the outermost, ℕ is the innermost.
  // We compute rx/ry for each set (index 4→0 as C→N)
  const levels = SETS.length; // 5
  const rects: { rx: number; ry: number }[] = SETS.map((_, i) => {
    const outerIdx = levels - 1 - i; // C=4→0, N=0→4
    const rx = W / 2 - PAD - outerIdx * PAD * 0.5;
    const ry = H / 2 - PAD - outerIdx * PAD * 0.5;
    return {
      rx: Math.max(rx, 20),
      ry: Math.max(ry, 12),
    };
  });

  // Build the geometry so we can store element refs for fast update.
  const setEls: Map<NumberSet, SVGGElement> = new Map();

  // Render ℂ first (back), then ℝ, ℚ, ℤ, ℕ (front) so inner ones sit on top.
  const renderOrder = [...SETS].reverse(); // C, R, Q, Z, N

  for (const { id, glyph } of renderOrder) {
    const idx = SETS.findIndex((e) => e.id === id); // 0=N..4=C
    const { rx, ry } = rects[idx]!;

    const g = createSvgEl('g', {
      class: 'nn-region',
      'data-set': id,
    });

    const rect = createSvgEl('rect', {
      class: 'nn-region-rect',
      x: cx - rx,
      y: cy - ry,
      width: rx * 2,
      height: ry * 2,
      rx: 12,
      ry: 12,
    });

    const label = createSvgEl('text', {
      class: 'nn-region-label',
      x: cx - rx + 14,
      y: cy - ry + 20,
      'text-anchor': 'start',
      'dominant-baseline': 'auto',
    });
    label.textContent = glyph;

    g.appendChild(rect);
    g.appendChild(label);
    group.appendChild(g);
    setEls.set(id, g);
  }

  function applyHighlight(): void {
    const { highlight } = current;
    const highlighted =
      highlight != null ? new Set<string>(supersetsOf(highlight)) : new Set<string>();
    for (const [id, el] of setEls) {
      if (highlighted.has(id)) {
        el.classList.add('is-highlight');
      } else {
        el.classList.remove('is-highlight');
      }
    }
  }

  applyHighlight();

  return {
    group,

    update(partial: Partial<NumberNestingOptions>): void {
      if ('highlight' in partial) {
        current = { ...current, highlight: partial.highlight ?? null };
      }
      applyHighlight();
    },

    destroy(): void {
      group.remove();
    },
  };
}
